const feed = document.querySelector("[data-suppliers-feed]");

if (feed) {
  const list = feed.querySelector("[data-suppliers-list]");
  const sentinel = feed.querySelector("[data-suppliers-sentinel]");
  const emptyState = feed.querySelector("[data-suppliers-empty]");
  const errorState = feed.querySelector("[data-suppliers-error]");
  const retryButton = feed.querySelector("[data-suppliers-retry]");

  if (!list) {
    throw new Error("Supplier feed list not found");
  }

  const apiUrl = list.dataset.apiUrl;
  if (!apiUrl) {
    throw new Error("Supplier feed requires data-api-url");
  }

  const stateKey = `suppliers-feed:${window.location.pathname}`;
  const limit = 20;
  let nextCursor = list.dataset.nextCursor || null;
  const previewMode = list.dataset.previewMode === "true";
  let isLoading = false;
  let observer = null;

  const requiredFields = [
    "id",
    "name",
    "slug",
    "emoji",
    "contactAddress",
    "supplierUrl"
  ];

  const cardDelayStep = 40;

  const getCardCount = () => list.querySelectorAll(".supplier-card").length;

  const weatherCard = feed.querySelector("[data-weather-card]");
  const weatherStatus = feed.querySelector("[data-weather-status]");
  const weatherBody = feed.querySelector("[data-weather-body]");
  const weatherIcon = feed.querySelector("[data-weather-icon]");
  const weatherTemp = feed.querySelector("[data-weather-temp]");
  const weatherCondition = feed.querySelector("[data-weather-condition]");
  const weatherMeta = feed.querySelector("[data-weather-meta]");
  const weatherLocation = feed.querySelector("[data-weather-location]");

  const assertWeather = (weather) => {
    if (!weather || typeof weather !== "object") {
      throw new Error("Weather payload missing");
    }

    const requiredFields = ["temperatureF", "lowF", "highF", "condition", "icon", "observedAt", "location"];
    requiredFields.forEach((field) => {
      const value = weather[field];
      if (value === undefined || value === null) {
        throw new Error(`Weather missing ${field}`);
      }
      if (typeof value === "string" && value.trim() === "") {
        throw new Error(`Weather missing ${field}`);
      }
    });

    if (!Number.isFinite(Number(weather.temperatureF))) {
      throw new Error("Weather temperatureF is not numeric");
    }
    if (!Number.isFinite(Number(weather.lowF))) {
      throw new Error("Weather lowF is not numeric");
    }
    if (!Number.isFinite(Number(weather.highF))) {
      throw new Error("Weather highF is not numeric");
    }
  };

  const renderWeather = (weather) => {
    if (!weatherBody || !weatherIcon || !weatherTemp || !weatherCondition || !weatherMeta || !weatherStatus || !weatherLocation) {
      throw new Error("Weather card elements missing");
    }

    const temperature = Math.round(Number(weather.temperatureF));
    const low = Math.round(Number(weather.lowF));
    const high = Math.round(Number(weather.highF));

    weatherIcon.textContent = weather.icon;
    weatherTemp.textContent = `${temperature}°F`;
    weatherCondition.textContent = weather.condition;
    weatherLocation.textContent = weather.location;
    weatherMeta.textContent = `Low ${low}°F \u2022 High ${high}°F`;
    weatherStatus.textContent = "Today";
    weatherBody.removeAttribute("aria-busy");
  };

  const loadWeather = async () => {
    if (!weatherCard) {
      return;
    }

    if (weatherBody) {
      weatherBody.setAttribute("aria-busy", "true");
    }

    const response = await fetch("/api/v1/weather");

    if (!response.ok) {
      throw new Error(`Weather request failed: ${response.status}`);
    }

    const payload = await response.json();
    assertWeather(payload);
    renderWeather(payload);
  };

  const showEmptyStateIfNeeded = () => {
    if (!emptyState) {
      return;
    }

    emptyState.hidden = getCardCount() !== 0;
  };

  const showErrorState = () => {
    if (!errorState) {
      return;
    }

    errorState.hidden = false;
  };

  const hideErrorState = () => {
    if (!errorState) {
      return;
    }

    errorState.hidden = true;
  };

  const saveState = () => {
    try {
      const payload = {
        path: window.location.pathname,
        html: list.innerHTML,
        nextCursor,
        scrollY: window.scrollY
      };
      sessionStorage.setItem(stateKey, JSON.stringify(payload));
    } catch (error) {
      console.warn("Unable to store supplier feed state", error);
    }
  };

  const restoreState = () => {
    let payload = null;

    try {
      const raw = sessionStorage.getItem(stateKey);
      if (!raw) {
        return false;
      }

      payload = JSON.parse(raw);
    } catch (error) {
      return false;
    }

    if (!payload || payload.path !== window.location.pathname) {
      return false;
    }

    if (typeof payload.html !== "string") {
      return false;
    }

    list.innerHTML = payload.html;
    nextCursor = payload.nextCursor || null;
    list.dataset.nextCursor = nextCursor || "";

    requestAnimationFrame(() => {
      window.scrollTo(0, payload.scrollY || 0);
    });

    return true;
  };

  const shouldRestore = () => {
    if (!performance.getEntriesByType) {
      return false;
    }

    const navigation = performance.getEntriesByType("navigation")[0];
    return navigation && navigation.type === "back_forward";
  };

  const createSkeletonCard = () => {
    const card = document.createElement("div");
    card.className = "supplier-card supplier-card--skeleton";
    card.setAttribute("aria-hidden", "true");

    const media = document.createElement("div");
    media.className = "supplier-card__media";

    const body = document.createElement("div");
    body.className = "supplier-card__body";

    const lineOne = document.createElement("div");
    lineOne.className = "supplier-card__skeleton-line";

    const lineTwo = document.createElement("div");
    lineTwo.className = "supplier-card__skeleton-line supplier-card__skeleton-line--short";

    body.appendChild(lineOne);
    body.appendChild(lineTwo);

    card.appendChild(media);
    card.appendChild(body);

    return card;
  };

  const addSkeletons = () => {
    const skeletons = [];
    const count = Math.max(3, Math.min(6, limit / 4));

    for (let i = 0; i < count; i += 1) {
      const skeleton = createSkeletonCard();
      skeletons.push(skeleton);
      list.appendChild(skeleton);
    }

    return skeletons;
  };

  const removeSkeletons = (skeletons) => {
    skeletons.forEach((skeleton) => skeleton.remove());
  };

  const assertSupplier = (supplier) => {
    if (!supplier || typeof supplier !== "object") {
      throw new Error("Supplier payload missing");
    }

    requiredFields.forEach((field) => {
      const value = supplier[field];
      if (value === undefined || value === null) {
        throw new Error(`Supplier missing ${field}`);
      }
      if (typeof value === "string" && value.trim() === "") {
        throw new Error(`Supplier missing ${field}`);
      }
    });
  };

  const buildSupplierCard = (supplier, index) => {
    assertSupplier(supplier);

    const card = document.createElement(previewMode ? "div" : "a");
    card.className = "supplier-card";
    if (!previewMode) {
      card.href = supplier.supplierUrl;
    }
    const delay = Math.min(index * cardDelayStep, 240);
    card.style.setProperty("--reveal-delay", `${delay}ms`);

    const media = document.createElement("div");
    media.className = "supplier-card__media";

    if (supplier.logoPath) {
      const img = document.createElement("img");
      img.src = supplier.logoPath;
      img.srcset = `${supplier.logoPath} 1x, ${supplier.logoPath} 2x`;
      img.sizes = "(max-width: 600px) 96px, 140px";
      img.alt = `${supplier.name} logo`;
      img.loading = "lazy";
      img.decoding = "async";
      media.appendChild(img);
    } else {
      const emoji = document.createElement("div");
      emoji.className = "supplier-card__emoji";
      emoji.setAttribute("aria-hidden", "true");
      emoji.textContent = supplier.emoji;
      media.appendChild(emoji);
    }

    const body = document.createElement("div");
    body.className = "supplier-card__body";

    const name = document.createElement("h2");
    name.className = "supplier-card__name";
    name.textContent = supplier.name;

    const meta = document.createElement("div");
    meta.className = "supplier-card__meta";

    if (supplier.venue) {
      const venue = document.createElement("span");
      venue.className = "supplier-card__venue";
      venue.textContent = supplier.venue;
      meta.appendChild(venue);

      const dot = document.createElement("span");
      dot.className = "supplier-card__dot";
      dot.textContent = "\u2022";
      meta.appendChild(dot);
    }

    const address = document.createElement("span");
    address.className = "supplier-card__address";
    address.textContent = supplier.contactAddress;
    meta.appendChild(address);

    body.appendChild(name);
    body.appendChild(meta);

    card.appendChild(media);
    card.appendChild(body);

    if (supplier.nextAvailableTodayLabel) {
      const aside = document.createElement("div");
      aside.className = "supplier-card__aside";

      const nextLabel = document.createElement("span");
      nextLabel.className = "supplier-card__next";
      nextLabel.textContent = supplier.nextAvailableTodayLabel;

      aside.appendChild(nextLabel);
      card.appendChild(aside);
    }

    return card;
  };

  const appendSuppliers = (suppliers) => {
    const startIndex = getCardCount();
    suppliers.forEach((supplier, index) => {
      const card = buildSupplierCard(supplier, startIndex + index);
      list.appendChild(card);
    });
  };

  const loadNextPage = async () => {
    if (isLoading || !nextCursor) {
      return;
    }

    isLoading = true;
    hideErrorState();

    const skeletons = addSkeletons();

    try {
      const response = await fetch(
        `${apiUrl}?cursor=${encodeURIComponent(nextCursor)}&limit=${limit}`
      );

      if (!response.ok) {
        throw new Error(`Supplier feed request failed: ${response.status}`);
      }

      const payload = await response.json();
      if (!payload || !Array.isArray(payload.suppliers)) {
        throw new Error("Supplier feed response invalid");
      }

      appendSuppliers(payload.suppliers);
      nextCursor = payload.nextCursor || null;
      list.dataset.nextCursor = nextCursor || "";

      if (!nextCursor && observer && sentinel) {
        observer.unobserve(sentinel);
      }
    } catch (error) {
      console.error(error);
      showErrorState();
    } finally {
      removeSkeletons(skeletons);
      isLoading = false;
      showEmptyStateIfNeeded();
    }
  };

  if (retryButton) {
    retryButton.addEventListener("click", () => {
      loadNextPage();
    });
  }

  if (previewMode) {
    list.addEventListener("click", (event) => {
      const card = event.target.closest(".supplier-card");
      if (!card) {
        return;
      }
      alert("Ticket purchases will be available when we launch. Stay tuned!");
    });
  }

  if (shouldRestore()) {
    restoreState();
  }

  if (weatherCard) {
    loadWeather().catch((error) => {
      console.error(error);
      if (weatherStatus) {
        weatherStatus.textContent = "Unavailable";
      }
      if (weatherCondition) {
        weatherCondition.textContent = "Weather unavailable";
      }
      if (weatherBody) {
        weatherBody.removeAttribute("aria-busy");
      }
    });
  }

  if (sentinel) {
    observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            loadNextPage();
          }
        });
      },
      {
        rootMargin: "600px 0px"
      }
    );

    observer.observe(sentinel);
  }

  showEmptyStateIfNeeded();

  window.addEventListener("pagehide", saveState);
}
